/**
 * Kody maszynowe błędów Teams — JEDNO miejsce, w którym front rozstrzyga, CO SIĘ STAŁO.
 *
 * DLACZEGO TEN PLIK ISTNIEJE (defekt zmierzony na produkcji 2026-09-11 22:17-22:23):
 * Microsoft Graph odrzucał JEDEN czat (czat spotkania `19:meeting_…@thread.v2`) błędem
 * `InsufficientPrivileges`, czyli HTTP 403 — „jesteś zalogowany, ale NIE MASZ UPRAWNIEŃ”.
 * Backend zamieniał to na ten sam wyjątek co wygaśnięcie tokenu, a front mówił człowiekowi
 * „Sesja Teams wygasła. Połącz ponownie”. Człowiek łączył konto raz za razem, dostawał to samo
 * i uznawał, że widget padł. Komunikat, który KŁAMIE o przyczynie, kosztuje więcej niż brak
 * komunikatu — bo wysyła po lekarstwo, które nie może pomóc.
 *
 * ROZSTRZYGAMY PO KODZIE Z SERWERA, NIE PO TREŚCI NAPISU. Napis wolno zmienić, poprawić
 * literówkę albo przetłumaczyć — i żadna z tych rzeczy nie ma prawa zmienić zachowania okna.
 *
 * ZGODNOŚĆ WSTECZ: dopóki backend nie odda kodu (stara odpowiedź), zachowujemy się dokładnie
 * jak dotąd — 409 `ms_connection_required` i 401 `teams_auth_expired` nadal znaczą „token wygasł”.
 */

export const KOD_TEAMS = {
    /** Token/połączenie Microsoft wygasło — ponowne połączenie konta MA SENS. */
    TOKEN_WYGASL: "teams_token_expired",
    /** Konto jest zalogowane, ale nie ma dostępu do TEJ rozmowy — ponowne łączenie NIC nie da. */
    BRAK_UPRAWNIEN: "teams_no_access",
    /** Graph nie odpowiedział (5xx, limit, przerwa) — warto ponowić za chwilę. */
    NIEDOSTEPNE: "teams_unavailable",
};

const ZNANE_KODY = new Set(Object.values(KOD_TEAMS));

/* Backend może umieścić kod w którymkolwiek z tych pól — czytamy wszystkie, zamiast zakładać
   jedno. Kod ZNANY ma pierwszeństwo przed rozpoznaniem po statusie: gdyby odpowiedź niosła
   jednocześnie stare `ms_connection_required` i nowy kod, wiążący jest nowy. */
const POLA_Z_KODEM = ["code", "kod", "error", "errorCode", "teamsCode", "kodBledu"];

/**
 * Kod maszynowy błędu Teams albo `null`, gdy odpowiedź go nie niesie i nie da się go
 * odtworzyć ze starego kontraktu.
 * @param {any} err błąd axiosa
 * @returns {string|null}
 */
export const kodBleduTeams = (err) => {
    const dane = err?.response?.data;
    if (dane && typeof dane === "object") {
        for (const pole of POLA_Z_KODEM) {
            const wartosc = dane[pole];
            if (typeof wartosc === "string" && ZNANE_KODY.has(wartosc)) return wartosc;
        }
    }

    // ── Stary kontrakt (odpowiedź bez kodu maszynowego) ──
    const status = err?.response?.status;
    if (status === 409 && dane?.code === "ms_connection_required") return KOD_TEAMS.TOKEN_WYGASL;
    if (status === 401 && dane?.error === "teams_auth_expired") return KOD_TEAMS.TOKEN_WYGASL;
    return null;
};

/** Czy przy tym błędzie wolno pokazać przycisk ponownego połączenia konta. */
export const czyProponowacPonownePolaczenie = (kod) => kod === KOD_TEAMS.TOKEN_WYGASL;

/**
 * Opis błędu dla człowieka.
 * @param {string|null} kod kod z {@link kodBleduTeams}
 * @param {{kontekst?: "lista"|"rozmowa"}} [opcje] czy mówimy o liście czatów, czy o jednej rozmowie
 * @returns {{tytul: string, tresc: string, ponownePolaczenie: boolean, ponowienie: boolean}}
 */
export const opisBleduTeams = (kod, { kontekst = "lista" } = {}) => {
    if (kod === KOD_TEAMS.TOKEN_WYGASL) {
        return {
            tytul: "Sesja Teams wygasła",
            tresc: "Połączenie z kontem Microsoft wygasło. Połącz konto ponownie, aby korzystać z czatów.",
            ponownePolaczenie: true,
            ponowienie: false,
        };
    }
    if (kod === KOD_TEAMS.BRAK_UPRAWNIEN) {
        return {
            tytul: "Brak uprawnień do tej rozmowy",
            tresc: kontekst === "rozmowa"
                ? "Twoje konto Microsoft nie ma dostępu do tej rozmowy. Ponowne łączenie konta tego nie zmieni — "
                  + "dostęp nadaje organizator rozmowy lub spotkania, a do czatów spotkań i kanałów także administrator "
                  + "Microsoft 365. Pozostałe rozmowy działają normalnie."
                : "Twoje konto Microsoft nie ma dostępu do części rozmów. Ponowne łączenie konta tego nie zmieni — "
                  + "dostęp nadaje organizator rozmowy lub administrator Microsoft 365.",
            /* 🔴 TO JEST SEDNO NAPRAWY: żadnego „Połącz ponownie”. */
            ponownePolaczenie: false,
            ponowienie: false,
        };
    }
    if (kod === KOD_TEAMS.NIEDOSTEPNE) {
        return {
            tytul: "Teams chwilowo nie odpowiada",
            tresc: "Microsoft Teams nie odpowiedział na zapytanie. Spróbuj ponownie za chwilę.",
            ponownePolaczenie: false,
            ponowienie: true,
        };
    }
    // Brak kodu (stara odpowiedź albo nieznany błąd) — zachowanie jak dotąd.
    return {
        tytul: kontekst === "rozmowa" ? "Nie udało się pobrać wiadomości" : "Nie udało się pobrać czatów",
        tresc: kontekst === "rozmowa"
            ? "Nie udało się pobrać wiadomości z tej rozmowy. Spróbuj ponownie."
            : "Nie udało się pobrać czatów. Spróbuj ponownie.",
        ponownePolaczenie: false,
        ponowienie: true,
    };
};

/* ── Pozycja listy oznaczona przez backend jako niedostępna ──────────────────────────────────
   Czat spotkania, do którego Graph nie daje wglądu, ma być WIDOCZNY i WYSZARZONY z podanym
   powodem, a nie klikalny — inaczej człowiek wchodzi w niego i dostaje błąd, którego nie umie
   naprawić. Czytamy kilka możliwych nazw pola, bo kształt DTO ustala równoległa fala backendu. */
/* `canReadMessages` to POLE Z KONTRAKTU backendu (TeamsChatDto) — Microsoft Graph odmawia odczytu
   wiadomości czatów spotkań, więc serwer zapowiada to na liście. Pozostałe nazwy czytamy na wypadek,
   gdyby kontrakt kiedyś urósł; brak pola = rozmowa dostępna, czyli zachowanie dotychczasowe. */
const POLA_NIEDOSTEPNOSCI = ["canReadMessages", "isAccessible", "dostepny", "isAvailable"];
const POLA_ODWROTNE = ["accessDenied", "niedostepny", "isUnavailable", "brakDostepu"];
const POLA_POWODU = ["accessReason", "unavailableReason", "powodNiedostepnosci", "powod", "reason"];

/** Czy backend oznaczył tę pozycję listy jako niedostępną. */
export const czatOznaczonyNiedostepnym = (chat) => {
    if (!chat || typeof chat !== "object") return false;
    for (const pole of POLA_NIEDOSTEPNOSCI) if (chat[pole] === false) return true;
    for (const pole of POLA_ODWROTNE) if (chat[pole] === true) return true;
    return false;
};

/** Powód niedostępności podany przez backend albo zdanie domyślne. */
export const powodNiedostepnosciCzatu = (chat) => {
    if (chat && typeof chat === "object") {
        for (const pole of POLA_POWODU) {
            const wartosc = chat[pole];
            if (typeof wartosc === "string" && wartosc.trim()) return wartosc.trim();
        }
        /* Czat spotkania to NAJCZĘSTSZY przypadek i ma konkretną przyczynę: Microsoft Graph nie
           udostępnia aplikacjom wiadomości czatu spotkania. Mówimy to wprost, zamiast zostawiać
           człowieka ze zdaniem „brak uprawnień”, po którym nie wie, kogo o co prosić. */
        if (chat.isMeetingChat === true || chat.czatSpotkania === true) {
            return "Czat spotkania — Microsoft Teams nie udostępnia jego wiadomości aplikacjom. Otwórz rozmowę w aplikacji Teams.";
        }
    }
    return "Brak uprawnień do tej rozmowy";
};
